/**
 * Error Tracking Utilities
 * Centralized error tracking for the admin app
 */

import { trackError } from './otel-browser';

/**
 * Global error handler setup
 */
export function setupGlobalErrorHandlers() {
  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    trackError(event.error || new Error(event.message), {
      'error.type': 'global_error',
      'error.filename': event.filename,
      'error.lineno': event.lineno,
      'error.colno': event.colno,
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));

    trackError(error, {
      'error.type': 'unhandled_promise_rejection',
    });
  });

  console.log('✅ Global error handlers configured');
}

/**
 * Track API error with context
 */
export function trackAPIError(
  error: Error,
  endpoint: string,
  method: string,
  statusCode?: number
) {
  trackError(error, {
    'error.category': 'api',
    'http.endpoint': endpoint,
    'http.method': method,
    'http.status_code': statusCode || 0,
  });
}

/**
 * Track validation error
 */
export function trackValidationError(
  error: Error,
  formName: string,
  fieldName?: string
) {
  trackError(error, {
    'error.category': 'validation',
    'form.name': formName,
    'form.field': fieldName || 'unknown',
  });
}

/**
 * Track component error
 */
export function trackComponentError(
  error: Error,
  componentName: string,
  props?: Record<string, any>
) {
  trackError(error, {
    'error.category': 'component',
    'component.name': componentName,
    'component.props': JSON.stringify(props || {}),
  });
}

/**
 * Track data processing error
 */
export function trackDataProcessingError(
  error: Error,
  operation: string,
  dataType: string
) {
  trackError(error, {
    'error.category': 'data_processing',
    'data.operation': operation,
    'data.type': dataType,
  });
}

/**
 * Track Redux error
 */
export function trackReduxError(
  error: Error,
  actionType: string,
  state?: any
) {
  trackError(error, {
    'error.category': 'redux',
    'redux.action_type': actionType,
    'redux.has_state': !!state,
  });
}

export default {
  setupGlobalErrorHandlers,
  trackAPIError,
  trackValidationError,
  trackComponentError,
  trackDataProcessingError,
  trackReduxError,
};

